import { useParams } from 'react-router-dom'
import { useAssetGroupLoader } from '../loaders'
import { AssetGroupType } from '../enums'
import NotFound from './error/NotFound'
import LoadingState from '../components/LoadingState'
import PageHeader from '../components/PageHeader'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  alpha,
  Box,
  Breadcrumbs,
  Chip,
  Container,
  darken,
  Grid,
  Link,
  Skeleton,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import { NavLink } from 'react-router-dom'
import { usePolicy } from '../hooks'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Bolt,
  ExpandMore,
  GppMaybe,
  LocalFireDepartment,
  Power,
  Search,
  Speed,
  Thermostat,
  Tune
} from '@mui/icons-material'
import {
  formatPower,
  formatPressure,
  formatTemperature,
  formatTemperatureDelta
} from '../utils/string'
import { Aurora, AuroraAssetGroup, AuroraAwhpEvent, AuroraAwhpData, Sensor } from '../types'
import AuroraStackCard from '../components/AuroraStackCard'
import { getSensorNow } from '../services/deviceService'
import { getAuroraAwhpActiveEvents, getAuroraAwhpDataNow } from '../services/aurora'
import { calculateAuroraElectricPower, calculateAuroraHeatOutput } from '../utils/aurora'
import { subMinutes } from 'date-fns'
import AuroraAssetGroupControls from '../components/AuroraAssetGroupControls'
import AuroraAssetGroupAlarmStatusChip from '../components/AuroraAssetGroupAlarmStatusChip'
import AuroraAssetGroupDefrostStatusChip from '../components/AuroraAssetGroupDefrostStatusChip'
import _ from 'lodash'
import { blue, red } from '@mui/material/colors'

function isAuroraAssetGroup(value: unknown): value is AuroraAssetGroup {
  if (typeof value !== 'object' || value === null || 'type' in value === false) {
    return false
  }

  return value.type === AssetGroupType.AURORA
}

export default function AuroraAssetGroupPage() {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The policy hook.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The AWHP data indexed by aurora ID.
   */
  const [auroraAwhpData, setAuroraAwhpData] = useState<Record<string, AuroraAwhpData>>({})

  /**
   * The sensors by indexed device ID.
   */
  const [sensors, setSensors] = useState<Record<string, Sensor>>({})

  /**
   * The active events indexed by aurora ID.
   */
  const [auroraAwhpActiveEvents, setAuroraAwhpActiveEvents] = useState<
    Record<string, AuroraAwhpEvent[]>
  >({})

  /**
   * The route params.
   */
  const { id } = useParams()

  /**
   * The asset group loader.
   */
  const { assetGroup, loading } = useAssetGroupLoader(id)

  /**
   * The auroras.
   */
  const auroras = useMemo(() => {
    if (assetGroup?.assets === undefined) {
      return []
    }

    return assetGroup.assets
      .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
      .map(({ aurora, ...asset }) => ({ ...aurora!, asset }) as Aurora)
  }, [assetGroup?.assets])

  const auroraIds = useMemo(() => auroras.map((aurora) => aurora.id), [auroras])

  const deviceIds = useMemo(
    () => auroras.map((aurora) => aurora.deviceId).filter((deviceId) => deviceId !== null),
    [auroras]
  )

  useEffect(() => {
    const controller = new AbortController()

    loadAuroraAwhpData(auroraIds, controller)
    loadAuroraAwhpActiveEvents(auroraIds, controller)
    loadSensors(deviceIds, controller)

    const ONE_MINUTE = 60 * 1000
    const interval = setInterval(() => {
      loadAuroraAwhpData(auroraIds, controller)
      loadAuroraAwhpActiveEvents(auroraIds, controller)
      loadSensors(deviceIds, controller)
    }, ONE_MINUTE)

    return () => {
      clearInterval(interval)
      controller.abort()
    }
  }, [auroraIds, deviceIds])

  async function loadAuroraAwhpData(auroraIds: string[], controller: AbortController) {
    const auroraAwhpData: Record<string, AuroraAwhpData> = {}

    for (const auroraId of auroraIds) {
      try {
        const auroraAwhpRecord = await getAuroraAwhpDataNow(auroraId, { signal: controller.signal })
        auroraAwhpData[auroraId] = auroraAwhpRecord
      } catch {
        // Ignore the error.
      }
    }

    setAuroraAwhpData(auroraAwhpData)
  }

  async function loadAuroraAwhpActiveEvents(auroraIds: string[], controller: AbortController) {
    const activeEvents: Record<string, AuroraAwhpEvent[]> = {}

    for (const auroraId of auroraIds) {
      try {
        activeEvents[auroraId] = await getAuroraAwhpActiveEvents(auroraId, {
          signal: controller.signal
        })
      } catch {
        // We can ignore the error.
      }
    }

    setAuroraAwhpActiveEvents(activeEvents)
  }

  async function loadSensors(deviceIds: string[], controller: AbortController) {
    const sensors: Record<string, Sensor> = {}

    for (const deviceId of deviceIds) {
      try {
        const sensor = await getSensorNow(deviceId, { signal: controller.signal })
        sensors[deviceId] = sensor
      } catch {
        // Ignore the error.
      }
    }

    setSensors(sensors)
  }

  // Check that the loaded asset group is actually an aurora asset group.
  if (!loading && assetGroup && !isAuroraAssetGroup(assetGroup)) {
    return <NotFound />
  }

  return (
    <Container maxWidth={false}>
      <PageHeader
        title={assetGroup?.name || t('loading')}
        breadcrumbs={
          <Breadcrumbs>
            <Link component={NavLink} to="/">
              {t('breadcrumbs:frontpage')}
            </Link>

            {assetGroup?.project ? (
              <Link component={NavLink} to="/projects">
                {t('breadcrumbs:projects')}
              </Link>
            ) : isSuperadmin ? (
              <Link component={NavLink} to="/auroras">
                Arctic Warm
              </Link>
            ) : null}

            {!assetGroup?.project && isSuperadmin && (
              <Link component={NavLink} to="/auroras#stacks">
                {t('auroraStacks')}
              </Link>
            )}

            {assetGroup?.project && (
              <Link component={NavLink} to={`/projects/${assetGroup.project.id}`}>
                {assetGroup.project.name}
              </Link>
            )}

            {assetGroup?.project && (
              <Link component={NavLink} to={`/projects/${assetGroup.project.id}#monitoring`}>
                {t('devices')}
              </Link>
            )}

            {assetGroup ? (
              <Typography title={assetGroup.name}>{assetGroup.name}</Typography>
            ) : (
              <Skeleton variant="text" width={100} />
            )}
          </Breadcrumbs>
        }
      />
      {loading ? (
        <LoadingState />
      ) : (
        <Grid container spacing={6}>
          {assetGroup && isAuroraAssetGroup(assetGroup) && (
            <Grid item xs={12}>
              <AuroraAssetGroupSystemStatus
                auroraAssetGroup={assetGroup}
                auroraAwhpData={auroraAwhpData}
                auroraAwhpActiveEvents={auroraAwhpActiveEvents}
                sensors={sensors}
              />
            </Grid>
          )}

          {assetGroup && isAuroraAssetGroup(assetGroup) && (
            <Grid item xs={12}>
              <AuroraAssetGroupStack
                auroraAssetGroup={assetGroup}
                auroraAwhpData={auroraAwhpData}
                auroraAwhpActiveEvents={auroraAwhpActiveEvents}
                sensors={sensors}
              />
            </Grid>
          )}

          {assetGroup && isAuroraAssetGroup(assetGroup) && (
            <Grid item xs={12}>
              <AuroraAssetGroupControls
                auroraAssetGroup={assetGroup}
                auroraAwhpData={auroraAwhpData}
                sensors={sensors}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  )
}

interface AuroraAssetGroupSystemStatusProps {
  auroraAssetGroup: AuroraAssetGroup
  auroraAwhpData: Record<string, AuroraAwhpData>
  auroraAwhpActiveEvents: Record<string, AuroraAwhpEvent[]>
  sensors: Record<string, Sensor>
}

function AuroraAssetGroupSystemStatus({
  auroraAssetGroup,
  auroraAwhpData,
  auroraAwhpActiveEvents,
  sensors
}: AuroraAssetGroupSystemStatusProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The theme hook.
   */
  const theme = useTheme()

  /**
   * The auroras.
   */
  const auroras = useMemo(() => {
    if (auroraAssetGroup?.assets === undefined) {
      return []
    }

    return auroraAssetGroup.assets
      .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
      .map(({ aurora, ...asset }) => ({ ...aurora!, asset }) as Aurora)
  }, [auroraAssetGroup?.assets])

  /**
   * The master aurora.
   */
  const masterAurora = useMemo(() => {
    if (auroras.length === 0 || auroraAssetGroup.customData?.masterId === undefined) {
      return null
    }

    return auroras.find((aurora) => aurora.id === auroraAssetGroup.customData.masterId) || null
  }, [auroras, auroraAssetGroup?.customData?.masterId])

  /**
   * The heat output.
   */
  const heatOutput = useMemo(() => {
    const values: number[] = []

    for (const aurora of auroras) {
      if (aurora.id in auroraAwhpData) {
        const value = calculateAuroraHeatOutput(aurora, auroraAwhpData[aurora.id])
        if (value !== null) {
          values.push(value)
        }
      }
    }

    if (values.length === 0) {
      return null
    }

    return values.reduce((sum, value) => sum + value, 0)
  }, [auroras, auroraAwhpData])

  /**
   * The total electric power.
   */
  const electricPower = useMemo(() => {
    const electricPowers: number[] = []

    for (const aurora of auroras) {
      if (aurora.id in auroraAwhpData) {
        const awhpData = auroraAwhpData[aurora.id]
        const sensor =
          aurora.deviceId !== null && aurora.deviceId in sensors ? sensors[aurora.deviceId] : null

        const electricPower = calculateAuroraElectricPower(aurora, awhpData, sensor)

        if (electricPower !== null) {
          electricPowers.push(electricPower)
        }
      }
    }

    if (electricPowers.length === 0) {
      return null
    }

    return electricPowers.reduce((sum, electricPower) => sum + electricPower, 0)
  }, [auroras, auroraAwhpData, sensors])

  const runningCount = useMemo(() => {
    let counter = 0
    for (const aurora of auroras) {
      if (aurora.id in auroraAwhpData) {
        const awhpReading = auroraAwhpData[aurora.id]
        if (new Date(awhpReading.time) > subMinutes(new Date(), 3)) {
          counter++
        }
      }
    }
    return counter
  }, [auroras, auroraAwhpData])

  const pressure = useMemo(() => {
    if (masterAurora === null || masterAurora.id in auroraAwhpData === false) {
      return null
    }

    return auroraAwhpData[masterAurora.id].pressure_value
  }, [masterAurora, auroraAwhpData])

  const outdoorTemperature = useMemo(() => {
    if (masterAurora === null || masterAurora.id in auroraAwhpData === false) {
      return null
    }

    return auroraAwhpData[masterAurora.id].temperature_outdoor
  }, [masterAurora, auroraAwhpData])

  const totalCount = auroras.length

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{
        background: theme.palette.background.paper,
        py: 4,
        px: 6,
        '& .MuiAlert-message': { width: '100%' }
      }}
    >
      <Stack justifyContent="space-between" gap={6} direction={{ xs: 'column', desktop: 'row' }}>
        <Stack direction="column" gap={2}>
          <Stack direction="row" gap={2} alignItems="center">
            <GppMaybe fontSize="large" />
            <Typography variant="h6">{t('systemStatus')}</Typography>
          </Stack>

          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" sx={{ pl: 10 }}>
            <AuroraAssetGroupAlarmStatusChip
              auroraAssetGroup={auroraAssetGroup}
              auroraAwhpActiveEvents={auroraAwhpActiveEvents}
              size="small"
            />

            <AuroraAssetGroupDefrostStatusChip
              auroraAssetGroup={auroraAssetGroup}
              auroraAwhpActiveEvents={auroraAwhpActiveEvents}
              size="small"
            />

            <Chip
              size="small"
              variant="outlined"
              label={`${runningCount}/${totalCount} ${t('running').toLowerCase()}`}
              color="success"
            />
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" gap={2}>
          <AuroraAssetGroupSystemStatusInfo
            title={t('heatOutput')}
            icon={<LocalFireDepartment />}
            value={formatPower(heatOutput, true, 1)}
          />

          <AuroraAssetGroupSystemStatusInfo
            title={t('electricPower')}
            icon={<Bolt />}
            value={formatPower(electricPower, true, 1)}
          />

          <AuroraAssetGroupSystemStatusInfo
            title={t('running')}
            icon={<Power />}
            value={`${runningCount}/${totalCount}`}
          />

          <AuroraAssetGroupSystemStatusInfo
            title={t('pressure')}
            icon={<Speed />}
            value={pressure !== null ? formatPressure(pressure, 'bar', 1) : '-'}
          />

          <AuroraAssetGroupSystemStatusInfo
            title={t('outdoorTemperature')}
            icon={<Thermostat />}
            value={outdoorTemperature !== null ? formatTemperature(outdoorTemperature) : '-'}
          />
        </Stack>
      </Stack>
    </Alert>
  )
}

interface AuroraAssetGroupSystemStatusInfoProps {
  title: string
  icon: ReactNode
  value: number | string
}

function AuroraAssetGroupSystemStatusInfo({
  title,
  icon,
  value
}: AuroraAssetGroupSystemStatusInfoProps) {
  /**
   * The theme hook.
   */
  const theme = useTheme()

  return (
    <Alert
      color="info"
      icon={icon}
      sx={{
        background: theme.palette.background.default,
        width: { xs: '100%', sm: 200 }
      }}
    >
      <Typography fontWeight={500} color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h4" fontWeight={400}>
        {value}
      </Typography>
    </Alert>
  )
}

interface AuroraAssetGroupStackProps {
  auroraAssetGroup: AuroraAssetGroup
  auroraAwhpData: Record<string, AuroraAwhpData>
  auroraAwhpActiveEvents: Record<string, AuroraAwhpEvent[]>
  sensors: Record<string, Sensor>
}

function AuroraAssetGroupStack({
  auroraAssetGroup,
  auroraAwhpData,
  auroraAwhpActiveEvents,
  sensors
}: AuroraAssetGroupStackProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The theme hook.
   */
  const theme = useTheme()

  /**
   * The auroras.
   */
  const auroras = useMemo(() => {
    if (auroraAssetGroup?.assets === undefined) {
      return []
    }

    return auroraAssetGroup.assets
      .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
      .map(({ aurora, ...asset }) => ({ ...aurora!, asset }) as Aurora)
  }, [auroraAssetGroup.assets])

  /**
   * The master ID.
   */
  const masterId = auroraAssetGroup.customData?.masterId || null

  /**
   * The master unit.
   */
  const master = useMemo(() => {
    if (auroras.length === 0 || masterId === null) {
      return null
    }

    return auroras.find((aurora) => aurora.id === masterId) || null
  }, [auroras, masterId])

  /**
   * The positions.
   */
  const positions = useMemo(() => {
    if (auroraAssetGroup.customData?.positions === undefined) {
      return []
    }

    return Object.values(auroraAssetGroup.customData.positions)
  }, [auroraAssetGroup.customData?.positions])

  /**
   * The position of the master unit in the stack.
   */
  const masterPosition = useMemo(() => {
    if (masterId === null || auroraAssetGroup.customData?.positions === undefined) {
      return null
    }

    return auroraAssetGroup.customData.positions[masterId] || null
  }, [auroraAssetGroup.customData.positions, masterId])

  const minX = positions.length > 0 ? Math.min(...positions.map((position) => position.x)) : 0

  const maxX = positions.length > 0 ? Math.max(...positions.map((position) => position.x)) : 0

  const columns = useMemo(() => {
    const columns = []

    for (let column = minX; column <= maxX; column++) {
      columns.push(column)
    }

    return columns
  }, [minX, maxX])

  const minY = positions.length > 0 ? Math.min(...positions.map((position) => position.y)) : 0

  const maxY = positions.length > 0 ? Math.max(...positions.map((position) => position.y)) : 0

  const rows = useMemo(() => {
    const rows = []

    for (let row = maxY; row >= minY; row--) {
      rows.push(row)
    }

    return rows
  }, [minY, maxY])

  function findAssetByStackPosition(row: number, column: number) {
    // Get the asset IDs that are in the given row and column.
    const auroraIds = Object.keys(auroraAssetGroup.customData.positions).filter(
      (id) =>
        auroraAssetGroup.customData.positions[id].y === row &&
        auroraAssetGroup.customData.positions[id].x === column
    )

    return auroraAssetGroup.assets?.find(
      (asset) => auroraIds.includes(asset.auroraId) || auroraIds.includes(asset?.aurora?.id)
    )
  }

  function Cell({ row, column }: { row: number; column: number }) {
    const asset = findAssetByStackPosition(row, column)

    const awhpData =
      asset?.aurora?.id !== undefined && asset.aurora.id in auroraAwhpData
        ? auroraAwhpData[asset.aurora.id]
        : null

    const awhpActiveEvents =
      asset?.aurora?.id !== undefined && asset.aurora.id in auroraAwhpActiveEvents
        ? auroraAwhpActiveEvents[asset.aurora.id]
        : []

    const sensor =
      asset?.aurora?.deviceId !== undefined &&
      asset.aurora.deviceId !== null &&
      asset.aurora.deviceId in sensors
        ? sensors[asset.aurora.deviceId]
        : null

    return (
      <Box
        sx={{
          width: 296,
          aspectRatio: '3 / 4',
          background: darken(theme.palette.background.default, 0.01),
          borderRadius: 2,
          flexShrink: { xs: 1, sm: 0 }
        }}
      >
        {asset && asset?.aurora && (
          <AuroraStackCard
            aurora={{ ...asset.aurora, asset: _.omit(asset, 'aurora') }}
            auroraAwhpData={awhpData}
            auroraAwhpActiveEvents={awhpActiveEvents}
            sensor={sensor}
          />
        )}
      </Box>
    )
  }

  function PipeLine({
    label,
    temperature,
    color
  }: {
    label: string
    temperature: number | null
    color: string
  }) {
    const direction = masterPosition !== null && masterPosition.x === 0 ? 'left' : 'right'

    return (
      <Stack direction="row" alignItems="center" gap={0}>
        <Box
          sx={{
            backgroundImage:
              direction === 'right'
                ? `linear-gradient(to right, ${alpha(color, 1)}, ${alpha(color, 0.2)})`
                : `linear-gradient(to right, ${alpha(color, 0)}, ${alpha(color, 0.2)})`,
            width: direction === 'right' ? '48px' : '100%',
            flexShrink: direction === 'right' ? 0 : 1,
            height: 12
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            flexGrow: 0,
            gap: 0,
            textAlign: 'center',
            background: theme.palette.background.default,
            py: 2,
            px: 4
          }}
        >
          {/*<Speed htmlColor={theme.palette.text.secondary} fontSize="small" sx={{ mb: -1 }} />*/}
          <Typography color="text.secondary">{label}</Typography>
          <Typography variant="h6">{formatTemperature(temperature)}</Typography>
        </Box>

        <Box
          sx={{
            backgroundImage:
              direction === 'right'
                ? `linear-gradient(to right, ${alpha(color, 0.2)}, ${alpha(color, 0)})`
                : `linear-gradient(to right, ${alpha(color, 0.2)}, ${alpha(color, 1)})`,
            width: direction === 'right' ? '100%' : '48px',
            flexShrink: direction === 'right' ? 1 : 0,
            height: 12
          }}
        />
      </Stack>
    )
  }

  function MasterBox() {
    if (master === null) {
      return <Box sx={{ width: 296, aspectRatio: '3 / 4', background: 'transparent' }} />
    }

    const direction = masterPosition !== null && masterPosition.x === 0 ? 'left' : 'right'

    const masterAwhpData =
      masterId !== null && masterId in auroraAwhpData ? auroraAwhpData[masterId] : null

    const supplyTemperature = masterAwhpData ? masterAwhpData.temperature_primary_supply : null

    const returnTemperature = masterAwhpData ? masterAwhpData.temperature_primary_return : null

    const deltaTemperature =
      supplyTemperature !== null && returnTemperature !== null
        ? supplyTemperature - returnTemperature
        : null

    return (
      <Box
        sx={{
          width: 296,
          aspectRatio: '3 / 4',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          borderRadius: 2,
          flexGrow: 0,
          flexShrink: { xs: 1, sm: 0 },
          position: 'relative'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'flex-end',
            gap: 2,
            top: 0,
            width: '100%',
            bottom: 0,
            left: direction === 'right' ? '-8px' : 'auto',
            right: direction === 'right' ? 'auto' : '-8px'
          }}
        >
          <PipeLine
            label={t('supplyTemperature')}
            temperature={supplyTemperature}
            color={red[400]}
          />

          {/*<Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: direction === 'right' ? 'flex-start' : 'flex-end',
              flexFlow: 'row',
              width: '120px',
              // height: '48px',
              textAlign: 'center',
              gap: 1,
              px: '48px',
              transform: direction === 'right' ? 'rotate(-90deg)' : 'rotate(90deg)',
              position: 'relative',
              left: direction === 'right' ? '-32px' : 'auto',
              right: direction === 'right' ? 'auto' : '-32px',
              transformOrigin: 'center center'
            }}
          >
            {deltaTemperature !== null && (
              <>
                <Typography color="text.secondary">ΔT</Typography>
                <Typography>{formatTemperatureDelta(deltaTemperature)}</Typography>
              </>
            )}
          </Box>*/}

          <PipeLine
            label={t('returnTemperature')}
            temperature={returnTemperature}
            color={blue[400]}
          />
        </Box>
      </Box>
    )
  }

  if (auroraAssetGroup.assets?.length === 0) {
    return (
      <Alert severity="info" icon={<Search />}>
        <AlertTitle>{t('noUnits')}</AlertTitle>
      </Alert>
    )
  }

  return (
    <Stack
      sx={{ flexDirection: 'column', flexWrap: 'nowrap', overflow: 'auto', width: '100%', gap: 2 }}
    >
      {rows.map((row) => (
        <Stack
          key={row}
          sx={{
            flexDirection: 'row',
            justifyContent: 'center',
            alignItem: 'center',
            flexWrap: 'nowrap',
            width: '100%',
            gap: 2
          }}
        >
          {master &&
          masterPosition !== null &&
          masterPosition.y === row &&
          masterPosition.x <= 0 ? (
            <MasterBox />
          ) : (
            <Box sx={{ width: 296, aspectRatio: '3 / 4', background: 'transparent' }} />
          )}

          {columns.map((column) => (
            <Cell key={`${row}-${column}`} row={row} column={column} />
          ))}

          {master && masterPosition !== null && masterPosition.y === row && masterPosition.x > 0 ? (
            <MasterBox />
          ) : (
            <Box sx={{ width: 296, aspectRatio: '3 / 4', background: 'transparent' }} />
          )}
        </Stack>
      ))}
    </Stack>
  )
}
